import { IcoCheck, IcoChevronRight, IcoClock, IcoUsers } from "../icons";

export type TeamMemberStatus = {
  id: string;
  name: string | null;
  email: string;
  hasSubmitted: boolean;
  submittedAt?: Date;
  submissionId?: string;
  formId?: string;
};

type TeamCheckInsCardProps = {
  members: TeamMemberStatus[];
  orgId: string;
};

export function TeamCheckInsCard({ members, orgId }: TeamCheckInsCardProps) {
  if (members.length === 0) {
    return null;
  }

  const submitted = members.filter((m) => m.hasSubmitted);
  const waiting = members.filter((m) => !m.hasSubmitted);
  const allComplete = waiting.length === 0;
  const completionPercent = Math.round(
    (submitted.length / members.length) * 100,
  );

  return (
    <section class="mt-8">
      <h2 class="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
        Your Team
      </h2>

      <div class="rounded-xl border border-border bg-surface overflow-hidden">
        {/* Header with progress */}
        <div class="px-5 py-4 border-b border-border">
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2.5">
              <div class="size-8 rounded-full bg-surface-muted flex items-center justify-center">
                <IcoUsers class="size-4 text-text-muted" />
              </div>
              <div>
                <span class="text-sm font-medium text-text">
                  {members.length} direct{" "}
                  {members.length === 1 ? "report" : "reports"}
                </span>
              </div>
            </div>
            <span
              class={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                allComplete
                  ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                  : "bg-amber-100/80 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
              }`}
            >
              {allComplete ? (
                <>
                  <IcoCheck class="size-3" />
                  All submitted
                </>
              ) : (
                `${submitted.length} of ${members.length}`
              )}
            </span>
          </div>

          {/* Progress bar */}
          <div class="h-1.5 bg-surface-muted rounded-full overflow-hidden">
            <div
              class={`h-full rounded-full transition-all duration-500 ease-out ${
                allComplete ? "bg-green-500" : "bg-[var(--brand-500)]"
              }`}
              style={{ width: `${completionPercent}%` }}
            />
          </div>
        </div>

        {/* Member list */}
        <div class="divide-y divide-border">
          {/* Waiting members first */}
          {waiting.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              orgId={orgId}
              status="waiting"
            />
          ))}

          {/* Submitted members */}
          {submitted.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              orgId={orgId}
              status="submitted"
            />
          ))}
        </div>

        {/* Footer link */}
        <a
          href={`/orgs/${orgId}/team`}
          class="group flex items-center justify-center gap-1 px-5 py-3 text-sm font-medium text-[var(--brand-600)] hover:text-[var(--brand-700)] hover:bg-surface-hover transition-colors duration-150 border-t border-border"
        >
          View full team
          <IcoChevronRight class="size-3.5 transition-transform duration-150 group-hover:translate-x-0.5" />
        </a>
      </div>
    </section>
  );
}

// ============================================================================
// Member Row
// ============================================================================

type MemberRowProps = {
  member: TeamMemberStatus;
  orgId: string;
  status: "waiting" | "submitted";
};

function MemberRow({ member, orgId, status }: MemberRowProps) {
  const displayName = member.name || member.email.split("@")[0] || "?";
  const initial = displayName.charAt(0).toUpperCase();

  // Link to submission if available, otherwise to team page
  const href =
    member.submissionId && member.formId
      ? `/orgs/${orgId}/forms/${member.formId}/submissions/${member.submissionId}`
      : `/orgs/${orgId}/team`;

  const hasSubmission = status === "submitted" && member.submissionId;

  // Clickable row for submitted members
  if (hasSubmission) {
    return (
      <a
        href={href}
        class="group flex items-center gap-3.5 px-5 py-3.5 hover:bg-surface-hover transition-colors duration-150"
      >
        {/* Avatar */}
        <div class="size-9 rounded-full flex items-center justify-center text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 flex-shrink-0">
          {initial}
        </div>

        {/* Name */}
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-text truncate leading-snug">
            {displayName}
          </p>
        </div>

        {/* Status */}
        <div class="flex items-center gap-2.5 flex-shrink-0">
          <span class="inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
            <IcoCheck class="size-3.5" />
            {member.submittedAt && formatShortDate(member.submittedAt)}
          </span>
          <IcoChevronRight class="size-4 text-text-subtle opacity-0 group-hover:opacity-100 transition-opacity duration-150" />
        </div>
      </a>
    );
  }

  // Non-clickable row for pending members
  return (
    <div class="flex items-center gap-3.5 px-5 py-3.5">
      {/* Avatar */}
      <div class="size-9 rounded-full flex items-center justify-center text-xs font-semibold bg-surface-muted text-text-muted flex-shrink-0">
        {initial}
      </div>

      {/* Name */}
      <div class="flex-1 min-w-0">
        <p class="text-sm font-medium text-text truncate leading-snug">
          {displayName}
        </p>
      </div>

      {/* Status */}
      <span class="inline-flex items-center gap-1 text-xs font-medium text-text-subtle">
        <IcoClock class="size-3.5" />
        Pending
      </span>
    </div>
  );
}

function formatShortDate(date: Date): string {
  const d = new Date(date);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
